import type { RiskReductionMeasure } from '@/shared/hooks/api/machineryDirectiveCompliance'
import type { RiskReductionMeasureListFilters } from './riskReductionMeasureListFilters'

export function measureDependsOnControlSystem(
  measure: RiskReductionMeasure
): boolean {
  return measure.type === 'SCP' && measure.dependsOnControlSystem === true
}

function measureHasNoEhsr(measure: RiskReductionMeasure): boolean {
  const clauseIds = measure.ehsrClauseIds
  return clauseIds == null || clauseIds.length === 0
}

function linkedHazardCount(measure: RiskReductionMeasure): number {
  return measure.linkedHazards?.length ?? 0
}

function matchesMeasureType(
  measure: RiskReductionMeasure,
  filters: RiskReductionMeasureListFilters
): boolean {
  if (filters.measureType === 'all') {
    return true
  }
  return measure.type === filters.measureType
}

function matchesSource(
  measure: RiskReductionMeasure,
  filters: RiskReductionMeasureListFilters
): boolean {
  if (filters.source === 'all') {
    return true
  }
  return measure.source === filters.source
}

function matchesEhsr(
  measure: RiskReductionMeasure,
  filters: RiskReductionMeasureListFilters
): boolean {
  const hasClauseFilter = filters.ehsrClauseIds.length > 0
  const hasNoEhsrFilter = filters.includeNoEhsr
  if (!hasClauseFilter && !hasNoEhsrFilter) {
    return true
  }

  let matches = false
  if (hasNoEhsrFilter && measureHasNoEhsr(measure)) {
    matches = true
  }
  if (hasClauseFilter) {
    const clauseIds = measure.ehsrClauseIds ?? []
    const hasSelectedClause = filters.ehsrClauseIds.some((clauseId) =>
      clauseIds.includes(clauseId)
    )
    if (hasSelectedClause) {
      matches = true
    }
  }
  return matches
}

function matchesDependsOnControlSystem(
  measure: RiskReductionMeasure,
  filters: RiskReductionMeasureListFilters
): boolean {
  if (filters.dependsOnControlSystem === 'all') {
    return true
  }
  if (filters.dependsOnControlSystem === 'yes') {
    return measureDependsOnControlSystem(measure)
  }
  return !measureDependsOnControlSystem(measure)
}

function matchesSafetyFunction(
  measure: RiskReductionMeasure,
  filters: RiskReductionMeasureListFilters
): boolean {
  if (filters.safetyFunction === 'all') {
    return true
  }
  if (!measureDependsOnControlSystem(measure)) {
    return false
  }
  if (filters.safetyFunction === 'assigned') {
    return measure.safetyFunctionId != null
  }
  return measure.safetyFunctionId == null
}

function matchesPlr(
  measure: RiskReductionMeasure,
  filters: RiskReductionMeasureListFilters
): boolean {
  if (filters.plr === 'all') {
    return true
  }
  if (filters.plr === 'none') {
    return measure.plr == null
  }
  return measure.plr === filters.plr
}

function matchesLinkedToHazards(
  measure: RiskReductionMeasure,
  filters: RiskReductionMeasureListFilters
): boolean {
  if (filters.linkedToHazards === 'all') {
    return true
  }
  const count = linkedHazardCount(measure)
  if (filters.linkedToHazards === 'linked') {
    return count > 0
  }
  return count === 0
}

export function filterRiskReductionMeasures(
  measures: RiskReductionMeasure[],
  filters: RiskReductionMeasureListFilters
): RiskReductionMeasure[] {
  return measures.filter(
    (measure) =>
      matchesMeasureType(measure, filters) &&
      matchesSource(measure, filters) &&
      matchesEhsr(measure, filters) &&
      matchesDependsOnControlSystem(measure, filters) &&
      matchesSafetyFunction(measure, filters) &&
      matchesPlr(measure, filters) &&
      matchesLinkedToHazards(measure, filters)
  )
}
